import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { 
  processProviderRedirect, 
  buildAuthorizationUrl,
  prepareTokenExchangeData,
  cleanupOAuthData,
} from '../utils/oauthUtils';
import { saveTokens } from '../utils/simpleTokens';
import { getProviderConfig, isProviderSupported } from '../utils/oauthConfig';

const apiUrl = process.env.REACT_APP_API_URL;

/**
 * Componente genérico para recibir redirecciones de proveedores OAuth
 * Ruta: /oauth/provider/:provider?p=...
 */
const ProviderCallback = () => {
  
  const [searchParams] = useSearchParams();
  const { provider } = useParams(); // Obtener proveedor de la URL
  const navigate = useNavigate();
  const [paramData, setParamData] = useState(null);
  const [rawParam, setRawParam] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [oauthResult, setOauthResult] = useState(null);
  const [providerConfig, setProviderConfig] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [processingTokens, setProcessingTokens] = useState(false);

  useEffect(() => {
    // Validar que el proveedor sea soportado
    if (!provider || !isProviderSupported(provider)) {
      setError(`Proveedor no soportado: ${provider || 'undefined'}`);
      return;
    }
    
    try {
      const config = getProviderConfig(provider);
      setProviderConfig(config);
      
      // Procesar parámetros según el proveedor
      const providerData = processProviderRedirect(searchParams, provider);
      
      if (providerData.code && providerData.state) {
        // Es un callback de OAuth - procesar tokens directamente
        procesarIntercambioTokens(providerData.code, providerData.state, config);
        return;
      }
      
      // Es una redirección inicial del proveedor - iniciar OAuth automáticamente
      setParamData(providerData);
      if (providerData.rawParam) {
        setRawParam(providerData.rawParam);
      }
      
      // Mostrar mensaje de autenticación y hacer petición automática después de 3 segundos
      setTimeout(() => {
        iniciarPeticionOAuth(providerData, config);
      }, 3000);
      
    } catch (error) {
      console.error(`Error procesando datos de ${provider}:`, error);
      setError(error.message);
    }
  }, [searchParams, provider, navigate]);

  const procesarIntercambioTokens = async (code, state, config) => {
    setProcessingTokens(true);
    setError('');
    
    try {
      
      // Preparar datos para el intercambio usando las utilidades genéricas
      const exchangeData = prepareTokenExchangeData(provider, code, state);
      
      // Llamar al backend para intercambiar tokens
      const backendUrl = `${apiUrl}/api/v1/oauth/${provider}/exchange`;
      
      const response = await fetch(backendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(exchangeData)
      });

      if (!response.ok) {
        throw new Error(`Error del backend (${response.status}): ${response.statusText}`);
      }

      const result = await response.json();
      
      setTokenData(result);
      
      // Guardar tokens en localStorage
      saveTokens(provider, result);
      
      setOauthResult({
        type: 'success',
        message: `¡Integración con ${config.name} realizada correctamente!`,
        timestamp: new Date().toISOString()
      });
      
      // Limpiar datos temporales de OAuth
      cleanupOAuthData(provider);
      
      // Redirigir a la lista de campos después de 3 segundos
      setTimeout(() => {
        navigate('/fields');
      }, 3000);
      
    } catch (error) {
      console.error(`Error procesando tokens de ${provider}:`, error);
      setError(`Error procesando intercambio: ${error.message}`);
      setOauthResult({
        type: 'error',
        message: error.message,
        timestamp: new Date().toISOString()
      });
    } finally {
      setProcessingTokens(false);
    }
  };

  const iniciarPeticionOAuth = async (providerData = null, config = null) => {
    const dataToUse = providerData || paramData;
    const configToUse = config || providerConfig;
    
    if (!dataToUse || !configToUse) {
      setError('No hay datos del proveedor para procesar');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Construir URL de autorización usando las utilidades genéricas
      const authUrl = await buildAuthorizationUrl(provider, dataToUse);

      // Mostrar información de la petición OAuth
      setOauthResult({
        type: 'redirect',
        authUrl,
        provider: configToUse.name,
        timestamp: new Date().toISOString()
      });

      // Redirigir directamente al proveedor OAuth
      window.location.href = authUrl;

    } catch (error) {
      console.error(`Error generando OAuth para ${provider}:`, error);
      setError('Error generando datos OAuth: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };


  // Si hay parámetro p (redirección inicial), mostrar solo mensaje de autenticación
  if (paramData && !tokenData && !error) {
    return (
      <div style={{ 
        padding: '40px', 
        maxWidth: '600px', 
        margin: '0 auto', 
        textAlign: 'center',
        minHeight: '50vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <div style={{ 
          backgroundColor: '#e3f2fd', 
          padding: '40px', 
          borderRadius: '12px',
          border: '2px solid #2196f3',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid #2196f3',
            borderTop: '4px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>
          
          <h2 style={{ margin: '20px 0', color: '#1976d2', fontSize: '24px' }}>
            🔐 Autenticando con {providerConfig?.name || 'IKOS'}
          </h2>
          
          <p style={{ fontSize: '16px', color: '#666', margin: '0' }}>
            Redirigiendo en unos segundos...
          </p>
          
          <style jsx>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Si hay resultado de éxito, mostrar mensaje simple y redirigir
  if (oauthResult?.type === 'success') {
    return (
      <div style={{ 
        padding: '40px', 
        maxWidth: '600px', 
        margin: '0 auto', 
        textAlign: 'center',
        minHeight: '50vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <div style={{ 
          backgroundColor: '#e8f5e8', 
          padding: '40px', 
          borderRadius: '12px',
          border: '2px solid #4caf50',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
          
          <h2 style={{ margin: '20px 0', color: '#2e7d32', fontSize: '24px' }}>
            ¡Integración realizada correctamente!
          </h2>
          
          <p style={{ fontSize: '16px', color: '#666', margin: '0' }}>
            Redirigiendo a la lista de campos...
          </p>
        </div>
      </div>
    );
  }

  // Si hay error, mostrar mensaje de error y quedarse ahí
  if (error) {
    return (
      <div style={{ 
        padding: '40px', 
        maxWidth: '600px', 
        margin: '0 auto', 
        textAlign: 'center',
        minHeight: '50vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <div style={{ 
          backgroundColor: '#ffebee', 
          padding: '40px', 
          borderRadius: '12px',
          border: '2px solid #f44336',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
          
          <h2 style={{ margin: '20px 0', color: '#c62828', fontSize: '24px' }}>
            Ha ocurrido un error
          </h2>
          
          <p style={{ fontSize: '16px', color: '#666', margin: '0', wordBreak: 'break-word' }}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  // Fallback para casos edge - normalmente no debería llegar aquí
  return null;
};

export default ProviderCallback;